// ForgotPasswordPage.tsx — step 1 of password reset: ask for a link.
//
// Full-canvas over the WebGL backdrop, same shell as VerifyEmailPage.
//
// The success copy deliberately does NOT confirm whether the address belongs to an
// account. The API answers identically either way on purpose — otherwise this page
// becomes a way for a stranger to ask "does this person have a TruXSocial account",
// and on a platform whose members are self-selected for not wanting to be catalogued,
// that is a question it should not answer.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Panel, TextField, Button } from '@/ui';
import { authService, RateLimited } from '@/core/services/authService';

const Wrap = styled.div`
  min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 32px;
`;
const Card = styled(Panel).attrs({ $brackets: false })`
  max-width: 440px; width: 100%; display: flex; flex-direction: column; gap: 14px;
`;
const Title = styled.h1`
  margin: 0; font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg}; color: ${({ theme }) => theme.color.textPrimary};
`;
const Body = styled.p`
  margin: 0; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.55;
`;
const ErrorText = styled.p`
  margin: 0; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.danger};
`;
const BackLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.accentCyan};
  text-decoration: none; align-self: center;
`;

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Validate on submit; never disable the button — a greyed control states no reason.
    if (!email.trim()) {
      setError('Enter the email address on your account.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await authService.requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      if (err instanceof RateLimited) {
        setError('Too many requests. Please wait a while before asking for another link.');
      } else {
        // Reaching here means the mail genuinely could not be sent. Saying so matters:
        // this is the only route back into a locked-out account, so a false "check
        // your inbox" would leave someone waiting on mail that will never arrive.
        setError('We could not send the email just now. Please try again in a few minutes.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <Wrap>
        <Card>
          <Title>Check your email</Title>
          <Body>
            If that address has a TruXSocial account, a reset link is on its way. The link
            works once and expires in an hour.
          </Body>
          <Body>
            If your account has two-factor authentication turned on, you will be asked for a
            code before the new password is set. An email on its own is not enough to take
            over an account.
          </Body>
          <BackLink to="/signin">Back to sign in</BackLink>
        </Card>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Card as="form" onSubmit={(e: React.FormEvent) => void handleSubmit(e)}>
        <Title>Reset your password</Title>
        <Body>
          Enter the email address on your account and we will send you a link to set a new
          password.
        </Body>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => { setEmail(e.currentTarget.value); setError(''); }}
        />
        {error && <ErrorText role="alert">{error}</ErrorText>}
        <Button type="submit" variant="primary">
          {busy ? 'Sending…' : 'Send reset link'}
        </Button>
        <BackLink to="/signin">Back to sign in</BackLink>
      </Card>
    </Wrap>
  );
}
